import type { Account } from '@/api/auth';
import { LmtWidgetType } from './live';

const SPORT_PROFILE_INDEX: Record<string, number> = {
  football: 0,
  soccer: 0,
  basketball: 1,
  tennis: 2,
};

function normalizeSportKey(sport: string): string {
  const key = sport.trim().toLowerCase().replace(/\s+/g, '');
  // API / menu sometimes use Soccer; legacy profile index is football.
  return key === 'soccer' ? 'football' : key;
}

function normalizeLang(lang: string): string {
  return lang === 'al' ? 'en' : lang;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Legacy profile keys are comma-separated per sport (football, basketball, tennis).
 * When a single key is provided it is used for every sport.
 */
export function resolveTheSportsProfileKey(keys: string, sport: string): string {
  const trimmed = keys.trim();
  if (!trimmed) return '';

  const parts = trimmed.split(',').map((part) => part.trim());
  if (parts.length === 1) return parts[0]!;

  const index = SPORT_PROFILE_INDEX[normalizeSportKey(sport)] ?? -1;
  if (index < 0) return '';
  return parts[index] ?? '';
}

export interface TheSportsWidgetUrlParams {
  lang: string;
  sport: string;
  profileKey: string;
  theSportsId: string;
}

/**
 * Legacy TheSports LMT embed via the c-lmt.com proxy (`wdg.html`), which rewrites
 * to tennis `2d` or other sports `all-round` widget URLs.
 */
export function buildTheSportsWidgetUrl({
  lang,
  sport,
  profileKey,
  theSportsId,
}: TheSportsWidgetUrlParams): string {
  const params = new URLSearchParams({
    language: normalizeLang(lang),
    sport: normalizeSportKey(sport),
    profile: profileKey,
    key: theSportsId,
  });
  return `https://c-lmt.com/app/html/wdg.html?${params.toString()}`;
}

/** Account has LiveStream master switch plus non-empty Url and Key (legacy IsLiveStreamConfigured). */
export function isLiveStreamConfigured(
  account: Pick<Account, 'LiveStream' | 'LiveStreamUrl' | 'LiveStreamKey'> | null | undefined,
): boolean {
  if (!account?.LiveStream) return false;
  if (!isNonEmptyString(account.LiveStreamUrl)) return false;
  if (!isNonEmptyString(account.LiveStreamKey)) return false;
  return true;
}

export interface ProviderStreamUrlParams {
  liveStreamUrl: string;
  liveStreamKey: string;
  theSportsId: string;
  lang: string;
}

/**
 * Client-side stream provider URL. Path is hardcoded to `/football` to match legacy.
 */
export function buildProviderStreamUrl({
  liveStreamUrl,
  liveStreamKey,
  theSportsId,
  lang,
}: ProviderStreamUrlParams): string | null {
  if (!isNonEmptyString(liveStreamUrl) || !isNonEmptyString(liveStreamKey)) return null;
  if (!isNonEmptyString(theSportsId)) return null;

  const base = liveStreamUrl.replace(/\/$/, '');
  const language = normalizeLang(lang);
  return `${base}/${language}/football?token=${encodeURIComponent(liveStreamKey)}&uuid=${encodeURIComponent(theSportsId)}`;
}

/** Wrap the provider URL in the legacy strm2.html iframe proxy. */
export function buildMediaIframeSrc(providerUrl: string | null | undefined): string | null {
  if (!isNonEmptyString(providerUrl)) return null;
  return `https://c-lmt.com/app/html/strm2.html?url=${encodeURIComponent(providerUrl)}`;
}

/**
 * Whether LMT can render for this event.
 * Matches legacy `LoadLiveWidget`: account `LiveMatchTrackingType` + key, and for
 * TheSports a non-null `TheSportsId` with a resolvable sport profile. Does **not**
 * gate on event `MatchTrackerEnabled` (legacy never checks that flag).
 */
export function canShowMatchTracker(params: {
  trackingType: number | string | null | undefined;
  trackingKey: string;
  theSportsId?: string | null;
  sport: string;
}): boolean {
  const trackingType = Number(params.trackingType);
  const trackingKey = params.trackingKey.trim();
  if (!Number.isFinite(trackingType) || trackingType === LmtWidgetType.None) return false;
  if (!trackingKey) return false;

  if (trackingType === LmtWidgetType.TheSports) {
    if (params.theSportsId == null || params.theSportsId === '') return false;
    return resolveTheSportsProfileKey(trackingKey, params.sport) !== '';
  }

  return trackingType === LmtWidgetType.Betradar;
}

type LiveStreamAccount = Pick<Account, 'LiveStream' | 'LiveStreamUrl' | 'LiveStreamKey'>;

/** Build the strm2 iframe src from account + event when stream is fully configured. */
export function buildLiveStreamIframeSrc(params: {
  account: LiveStreamAccount | null | undefined;
  theSportsId?: string | null;
  lang: string;
}): string | null {
  const { account, theSportsId, lang } = params;
  if (!account || !isLiveStreamConfigured(account) || !theSportsId) return null;
  const providerUrl = buildProviderStreamUrl({
    liveStreamUrl: account.LiveStreamUrl!,
    liveStreamKey: account.LiveStreamKey!,
    theSportsId,
    lang,
  });
  return buildMediaIframeSrc(providerUrl);
}
